import React, { useState, useEffect } from 'react';
import { useWallet } from '../../contexts/WalletContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import './PriceTargetModal.css';

const PriceTargetModal = ({
    isOpen,
    onClose,
    token,
    userBalance = 0,
    currentPrice = 0
}) => {
    const { selectedWallet } = useWallet();
    const { user } = useAuth();
    const { showToast } = useToast();

    const [formData, setFormData] = useState({
        targetType: 'buy',
        targetPrice: '',
        amount: '',
        amountType: 'token',
        priority: 1,
        slippageBps: 100,
        expiresAt: ''
    });

    const [loading, setLoading] = useState(false);
    // Local setter to allow updates when fetching latest price
    const [, setCurrentPrice] = useState(currentPrice);
    const [errors, setErrors] = useState({});
    const [priceTargets, setPriceTargets] = useState([]);
    const [priceLoading, setPriceLoading] = useState(false);

    useEffect(() => {
        if (isOpen && token) {
            // Reset form when modal opens
            setFormData({
                targetType: 'buy',
                targetPrice: currentPrice ? (currentPrice * 0.95).toFixed(6) : '',
                amount: '',
                amountType: 'token',
                priority: 1,
                slippageBps: 100,
                expiresAt: ''
            });
            setErrors({});
            loadUserPriceTargets();
        }
    }, [isOpen, token, currentPrice]);

    const loadUserPriceTargets = async () => {
        if (!user?.id) return;

        try {
            const response = await fetch(`/api/price-targets/user/${user.id}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setPriceTargets(data.data || []);
            }
        } catch (error) {
            console.error('Error loading price targets:', error);
        }
    };

    const fetchCurrentPrice = async () => {
        if (!token?.address) return;
        setPriceLoading(true);
        try {
            let url;
            if (token.address.toLowerCase() === 'sol') {
                url = `https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd`;
            } else {
                url = `https://api.coingecko.com/api/v3/simple/token_price/solana?contract_addresses=${token.address}&vs_currencies=usd`;
            }
            const response = await fetch(url);
            const data = await response.json();
            let price = null;
            if (token.address.toLowerCase() === 'sol') {
                price = data['solana']?.usd;
            } else {
                const key = Object.keys(data)[0];
                price = data[key]?.usd;
            }
            if (price) {
                setCurrentPrice(parseFloat(price));
            }
        } catch (error) {
            console.error('Error fetching current price:', error);
        } finally {
            setPriceLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.targetPrice || parseFloat(formData.targetPrice) <= 0) {
            newErrors.targetPrice = 'Valid target price is required';
        }

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Valid amount is required';
        }

        // Validate sell orders have sufficient balance
        if (formData.targetType === 'sell') {
            const amount = parseFloat(formData.amount);
            if (formData.amountType === 'token' && amount > userBalance) {
                newErrors.amount = `Insufficient balance. Available: ${userBalance.toFixed(6)}`;
            } else if (formData.amountType === 'percentage' && amount > 100) {
                newErrors.amount = 'Percentage cannot exceed 100%';
            }
        }

        // Validate price makes sense
        if (currentPrice && formData.targetPrice) {
            const targetPrice = parseFloat(formData.targetPrice);
            const priceDiff = Math.abs(targetPrice - currentPrice) / currentPrice * 100;

            if (priceDiff > 90) {
                newErrors.targetPrice = 'Target price seems unrealistic (>90% difference)';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm() || !selectedWallet || !user?.id) {
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('/api/price-targets', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    tokenAddress: token.address,
                    walletId: selectedWallet.id,
                    ...formData,
                    targetPrice: parseFloat(formData.targetPrice),
                    amount: parseFloat(formData.amount),
                    priority: parseInt(formData.priority),
                    slippageBps: parseInt(formData.slippageBps),
                    expiresAt: formData.expiresAt || null
                })
            });

            const data = await response.json();

            if (data.success) {
                showToast('Price target created successfully', 'success');
                loadUserPriceTargets(); // Refresh the list

                // Reset form
                setFormData({
                    targetType: 'buy',
                    targetPrice: currentPrice ? (currentPrice * 0.95).toFixed(6) : '',
                    amount: '',
                    amountType: 'token',
                    priority: 1,
                    slippageBps: 100,
                    expiresAt: ''
                });
            } else {
                showToast(data.error || 'Failed to create price target', 'error');
            }

        } catch (error) {
            console.error('Error creating price target:', error);
            showToast('Failed to create price target', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelTarget = async (targetId) => {
        try {
            const response = await fetch(`/api/price-targets/${targetId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const data = await response.json();

            if (data.success) {
                showToast('Price target cancelled', 'success');
                loadUserPriceTargets();
            } else {
                showToast(data.error || 'Failed to cancel price target', 'error');
            }

        } catch (error) {
            console.error('Error cancelling price target:', error);
            showToast('Failed to cancel price target', 'error');
        }
    };

    const formatPrice = (price) => {
        return parseFloat(price).toFixed(6);
    };

    const formatAmount = (target) => {
        const amount = parseFloat(target.amount);
        switch (target.amount_type) {
            case 'token':
                return `${amount.toFixed(6)} ${token?.symbol || 'tokens'}`;
            case 'sol':
                return `${amount.toFixed(3)} SOL`;
            case 'percentage':
                return `${amount}%`;
            default:
                return amount.toString();
        }
    };

    const getPriorityLabel = (priority) => {
        switch (priority) {
            case 1: return 'High';
            case 2: return 'Medium';
            case 3: return 'Low';
            default: return priority.toString();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="price-target-modal" onClick={e => e.stopPropagation()}
                style={{}}
                >
                <div className="modal-header">
                    <h3>Price Targets - {token?.symbol}</h3>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="" style={{
                    // background:'green',
                    padding:'9%',
                    paddingTop:'3%'
                }}>
                    <div className="token-info"
                    style={{
                        display:'flex',
                        flexDirection:'row'
                    }}
                    >
                        <div className="token-identity" >
                            <img src={token?.logoURI} alt={token?.symbol} className="token-logo" />
                            <div>
                                <div className="token-symbol">{token?.symbol}</div>
                                <div className="token-name">{token?.name}</div>
                            </div>
                        </div>
                        <div className="price-info">
                            <div className="current-price">
                                Current: ${formatPrice(currentPrice)}
                            </div>
                            {userBalance > 0 && (
                                <div className="user-balance">
                                    Balance: {userBalance.toFixed(6)} {token?.symbol}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="price-target-sections" style={{
                        display:'flex', flexDirection:'column',
                        gap:'20px', justifyContent:'space-between'
                    }}>
                        {/* Create New Target Section */}
                        <div className="create-target-section">
                            <h4>Create Price Target...</h4>

                            <form onSubmit={handleSubmit} className="price-target-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Order Type</label>
                                        <select
                                            value={formData.targetType}
                                            onChange={(e) => setFormData({...formData, targetType: e.target.value})}
                                        >
                                            <option value="buy">Buy Order</option>
                                            <option value="sell">Sell Order</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Priority</label>
                                        <select
                                            value={formData.priority}
                                            onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                                        >
                                            <option value={1}>High (1)</option>
                                            <option value={2}>Medium (2)</option>
                                            <option value={3}>Low (3)</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Target Price ($)</label>
                                        <input
                                            type="number"
                                            step="0.000001"
                                            value={formData.targetPrice}
                                            onChange={(e) => setFormData({...formData, targetPrice: e.target.value})}
                                            placeholder="0.000000"
                                            className={errors.targetPrice ? 'error' : ''}
                                        />
                                        {errors.targetPrice && (
                                            <span className="error-text">{errors.targetPrice}</span>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label>Amount Type</label>
                                        <select
                                            value={formData.amountType}
                                            onChange={(e) => setFormData({...formData, amountType: e.target.value})}
                                        >
                                            <option value="token">Token Amount</option>
                                            <option value="sol">SOL Value</option>
                                            {formData.targetType === 'sell' && (
                                                <option value="percentage">Percentage</option>
                                            )}
                                        </select>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>
                                            Amount {
                                                formData.amountType === 'token' ? `(${token?.symbol})` :
                                                formData.amountType === 'sol' ? '(SOL)' :
                                                '(%)'
                                            }
                                        </label>
                                        <input
                                            type="number"
                                            step={formData.amountType === 'percentage' ? '1' : '0.000001'}
                                            value={formData.amount}
                                            onChange={(e) => setFormData({...formData, amount: e.target.value})}
                                            placeholder={
                                                formData.amountType === 'token' ? '0.000000' :
                                                formData.amountType === 'sol' ? '0.000' :
                                                '0'
                                            }
                                            className={errors.amount ? 'error' : ''}
                                        />
                                        {errors.amount && (
                                            <span className="error-text">{errors.amount}</span>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label>Slippage (%)</label>
                                        <select
                                            value={formData.slippageBps}
                                            onChange={(e) => setFormData({...formData, slippageBps: parseInt(e.target.value)})}
                                        >
                                            <option value={50}>0.5%</option>
                                            <option value={100}>1%</option>
                                            <option value={300}>3%</option>
                                            <option value={500}>5%</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Expiration (Optional)</label>
                                    <input
                                        type="datetime-local"
                                        value={formData.expiresAt}
                                        onChange={(e) => setFormData({...formData, expiresAt: e.target.value})}
                                        min={new Date().toISOString().slice(0, 16)}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="create-target-btn"
                                    disabled={loading}
                                >
                                    {loading ? 'Creating...' : 'Create Price Target'}
                                </button>
                            </form>
                        </div>

                        {/* Active Targets Section */}
                        <div className="active-targets-section">
                            <h4>Active Price Targets ({priceTargets.length})</h4>

                            {priceTargets.length === 0 ? (
                                <div className="no-targets">
                                    No active price targets for this token
                                </div>
                            ) : (
                                <div className="targets-list">
                                    {priceTargets.map(target => (
                                        <div key={target.id} className="target-item">
                                            <div className="target-info">
                                                <div className="target-header">
                                                    <span className={`target-type ${target.target_type}`}>
                                                        {target.target_type.toUpperCase()}
                                                    </span>
                                                    <span className="target-priority">
                                                        {getPriorityLabel(target.priority)} Priority
                                                    </span>
                                                </div>

                                                <div className="target-details">
                                                    <div className="detail">
                                                        <span className="label">Target Price:</span>
                                                        <span className="value">${formatPrice(target.target_price)}</span>
                                                    </div>
                                                    <div className="detail">
                                                        <span className="label">Amount:</span>
                                                        <span className="value">{formatAmount(target)}</span>
                                                    </div>
                                                    <div className="detail">
                                                        <span className="label">Created:</span>
                                                        <span className="value">
                                                            {new Date(target.created_at).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                    {target.expires_at && (
                                                        <div className="detail">
                                                            <span className="label">Expires:</span>
                                                            <span className="value">
                                                                {new Date(target.expires_at).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <button
                                                className="cancel-target-btn"
                                                onClick={() => handleCancelTarget(target.id)}
                                                title="Cancel this price target"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PriceTargetModal;
